import flatMap from 'lodash/flatMap'
import get from 'lodash/get'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import uniqBy from 'lodash/uniqBy'
import { createReducer } from 'redux-create-reducer'
import type { Contact, PlaylistActivity, RecipientList } from 'types/list'
import type { Playlist } from 'types/playlist'
import {
  CAMPAIGN_LIST_BEGIN_EDIT,
  CONTACT_ACTIVITY_LOAD,
  CONTACT_ACTIVITY_LOAD_FAIL,
  CONTACT_ACTIVITY_LOAD_SUCCESS,
  CONTACT_NOTES_CREATE_SUCCESS,
  CONTACT_NOTES_DELETE_SUCCESS,
  CONTACT_NOTES_LOAD_SUCCESS,
  CONTACT_NOTES_UPDATE_SUCCESS,
  CONTACTS_ADD_WARNING,
  CONTACTS_CANCEL_EDITING,
  CONTACTS_CREATE,
  CONTACTS_CREATE_FAIL,
  CONTACTS_CREATE_SUCCESS,
  CONTACTS_DELETE_SUCCESS,
  CONTACTS_IMPORT_SUCCESS,
  CONTACTS_LOAD,
  CONTACTS_LOAD_FAIL,
  CONTACTS_LOAD_IDS,
  CONTACTS_LOAD_IDS_FAIL,
  CONTACTS_LOAD_IDS_NEXT_SUCCESS,
  CONTACTS_LOAD_IDS_SUCCESS,
  CONTACTS_LOAD_SUCCESS,
  CONTACTS_MODE_SWITCH,
  CONTACTS_REMOVE_WARNING,
  CONTACTS_SEARCH,
  CONTACTS_SET_COLUMN_MAPPING,
  CONTACTS_SORT,
  CONTACTS_TOGGLE,
  CONTACTS_UNSUBSCRIBE_SUCCESS,
  CONTACTS_UPDATE,
  CONTACTS_UPDATE_ALL_SUCCESS,
  CONTACTS_UPDATE_FAIL,
  CONTACTS_UPDATE_SUCCESS,
  CONTACTS_UPDATE_TAGS_SUCCESS,
  CONTACTS_VALUE_ADD,
  CONTACTS_VALUE_CHANGE,
  CONTACTS_VALUE_REMOVE,
  DATA_LOAD,
  EMAIL_GROUP_CREATE_SUCCESS,
  EMAIL_GROUP_UPDATE_SUCCESS,
  PLAYLIST_SHARE_SUCCESS,
  PLAYLIST_SHARES_LOADING_SUCCESS,
} from '@disco/constants/ActionTypes'
import { SORT_DIRECTION_ASC } from '@disco/constants/SortTypes'
import { updateItem } from '@disco/utils/immutable'
import { areTagsIncludeValues } from '@disco/utils/tags'
// TODO
export type State = Partial<typeof initialState>
const initialState = {
  loaded: false,
  loading: false,
  error: null,
  contacts: [] as Array<Contact>,
  sortParam: 'Name',
  sortDirection: SORT_DIRECTION_ASC,
  importError: null,
  columnMapping: null,
  searchTerm: '',
  // identifies which list/append-mode context searchTerm was captured in
  // (e.g. a list id, or 'all'), so a search from one context is never
  // reused as if it were typed in a different one
  searchContext: null as string | null,
  saving: false,
  next: '',
  ids: [] as any[],
  selectedAll: false,
  // ids excluded from an active "select all" by individual row toggles
  deselectedIds: [] as number[],
  warning: null,
}

const switchToEditMode = (contacts, ids, batchMode) => {
  return contacts.map(c => {
    if (ids.includes(c.id)) {
      const editMode = batchMode ? true : !c.editMode
      // if referenced_business is present, user shouldn't be able to edit
      // contact
      return {
        ...c,
        editMode: c.referenced_business ? false : editMode,
        selected: !batchMode && c.editMode ? false : c.selected,
      }
    }

    // if no changes - do nothing
    if (!c.editMode && !c.selected && !c.pendingChanges) return c
    return { ...c, editMode: false, selected: false, pendingChanges: null }
  })
}

const updateContact = (items, id, updater: (arg0: any) => Contact) => {
  const updatedItemIndex = items.findIndex(item => item.id === id)
  return [
    ...items.slice(0, updatedItemIndex),
    updater(items[updatedItemIndex]),
    ...items.slice(updatedItemIndex + 1),
  ]
}

const updateContacts = (
  oldContacts: Array<Contact>,
  newContacts: Array<Contact>,
  newActivityItem: PlaylistActivity | null | undefined = undefined,
  partialUpdate?: string[],
) => {
  const oldIds = oldContacts.map(c => c.id)
  const toAdd = newContacts.filter(c => !oldIds.includes(c.id))
  const updatedOldContacts = oldContacts.map(c => {
    const updatedContact = newContacts.find(
      updatedContact => c.id === updatedContact.id,
    )
    let { activity } = c

    if (!activity && updatedContact && updatedContact.activity) {
      activity = updatedContact.activity
    }

    if (activity && newActivityItem) {
      const addingId = newActivityItem.id
      const exists = activity.find(a => a.id === addingId)

      if (!exists) {
        activity = activity.concat([newActivityItem])
      }
    }

    // partial updates doesn't change editMode and doesn't reset pendingChanges
    if (partialUpdate) {
      if (updatedContact) {
        return {
          ...c,
          ...updatedContact,
          ...pick(updatedContact.pendingChanges, partialUpdate),
          activity,
          pendingChanges: omit(updatedContact.pendingChanges, partialUpdate),
        }
      }

      return c
    }

    if (updatedContact) {
      // merge existing contact details with updated contact as api
      // doesn't return full details(pros, acts, publishers) in bulk_update
      // response
      return {
        ...c,
        ...updatedContact,
        ...updatedContact.pendingChanges,
        activity,
        pendingChanges: null,
        editMode: false,
      }
    }

    return { ...c, activity, pendingChanges: null, editMode: false }
  })
  // should updatedOldContacts+toAdd be sorted?
  return updatedOldContacts.concat(toAdd)
}

function contactsFromList(
  list: RecipientList,
  playlist?: Playlist,
): Array<Contact> {
  const contacts: Array<Contact> = list.recipients
    .map(rec => rec.contact)
    .filter((c): c is Contact => c != null)

  if (playlist) {
    const playlistId = playlist.id
    // @ts-expect-error - TODO: fix this
    return contacts.map(contact => {
      if (contact && contact.activity) {
        const activity = contact.activity
        const existingPlaylist = activity.some(pl => pl.id === playlistId)

        if (!existingPlaylist) {
          return {
            ...contact,
            // @ts-expect-error - TODO: fix this
            activity: [{ ...playlist, tracks: [] }].concat(activity),
          }
        } else {
          return contact
        }
      } else {
        return contact
      }
    })
  }

  return contacts
}

const updateContactsValue = (
  state,
  id,
  field,
  value,
  operationType: 'replace' | 'add' | 'remove',
) => {
  return {
    ...state,
    contacts: (state.contacts ?? []).map(contact => {
      let newValue = value

      if (operationType === 'add' || operationType === 'remove') {
        const currentValue = get(
          contact,
          `pendingChanges[${field}]`,
          contact[field] || [],
        )
        if (operationType === 'remove')
          newValue = currentValue.filter(v => v !== value)
        if (operationType === 'add')
          newValue = currentValue.includes(value)
            ? currentValue
            : [...currentValue, value]
      }

      let pendingChanges = { ...contact.pendingChanges, [field]: newValue }

      // reset fields that relevant only to artist and writer types if
      // other type is chosen
      if (
        field === 'tags' &&
        !areTagsIncludeValues(newValue, ['Writer', 'Artist'], 'any')
      ) {
        // pendingChanges.pros = []
        // pendingChanges.publishers = []
        // pendingChanges.acts = ''
        pendingChanges = {
          ...pendingChanges,
          pros: [],
          publishers: [],
          acts: '',
        }
      }

      // we should change all records that are in the edit mode
      if (contact.id === id || (field !== 'selected' && contact.editMode)) {
        return { ...contact, pendingChanges }
      }

      return contact
    }),
  }
}

export const contactsReducer = createReducer<State, any>(initialState, {
  [CONTACTS_SET_COLUMN_MAPPING](state, { error, columnMapping }) {
    return { ...state, columnMapping, importError: error }
  },

  [CONTACTS_CREATE](state) {
    return { ...state, saving: true }
  },

  [CONTACTS_UPDATE](state) {
    return { ...state, saving: true }
  },

  [CONTACTS_CREATE_FAIL](state) {
    return { ...state, saving: false }
  },

  [CONTACTS_UPDATE_FAIL](state) {
    return { ...state, saving: false }
  },

  [CONTACTS_LOAD](state) {
    return { ...state, loading: true }
  },

  [CONTACTS_LOAD_SUCCESS](state, { contacts, next }) {
    const stateContacts = state.contacts ?? []
    const excluded = new Set(state.deselectedIds ?? [])
    const selectedIds = state.selectedAll
      ? new Set((state.ids ?? []).filter(id => !excluded.has(id)))
      : null
    const loadedContacts = contacts.map(c => {
      // ensure that we keep loaded activity and notes if contact were
      // loaded before full contact list
      const foundContact = stateContacts.find(
        existingContact => c.id === existingContact.id,
      )
      const allInEditMode =
        stateContacts.length && !stateContacts.some(c => !c.editMode)
      const selected =
        (foundContact?.selected ?? false) || (selectedIds?.has(c.id) ?? false)
      return foundContact
        ? {
            ...foundContact,
            ...c,
            selected,
            editMode: allInEditMode || false,
            loaded: true,
          }
        : {
            ...c,
            selected,
            editMode: allInEditMode || false,
            loaded: true,
          }
    })
    const updatedContacts = uniqBy([...stateContacts, ...loadedContacts], 'id')
    return {
      ...state,
      loading: false,
      error: null,
      contacts: updatedContacts,
      next,
    }
  },

  [CONTACTS_LOAD_FAIL](state, { error }) {
    return { ...state, loading: false, error }
  },

  [CONTACTS_LOAD_IDS](state) {
    return { ...state, loaded: false, loading: true }
  },

  [CONTACTS_LOAD_IDS_SUCCESS](state, { ids }) {
    return { ...state, loading: false, loaded: true, error: null, ids: ids }
  },

  [CONTACTS_LOAD_IDS_FAIL](state, { error }) {
    return { ...state, loaded: false, loading: false, error }
  },

  [CONTACTS_LOAD_IDS_NEXT_SUCCESS](state, { ids }) {
    // ids here is the complete accumulated list, not an increment
    return {
      ...state,
      loading: false,
      loaded: true,
      error: null,
      ids: [...new Set(ids)],
    }
  },

  [CONTACTS_SORT](state, action) {
    const { sortParam, sortDirection } = action
    return { ...state, sortDirection, sortParam }
  },

  [CONTACTS_SEARCH](state, action) {
    const { searchTerm, searchContext } = action
    return { ...state, searchTerm, searchContext: searchContext ?? null }
  },

  [CAMPAIGN_LIST_BEGIN_EDIT](state) {
    return { ...state, searchTerm: '', searchContext: null }
  },

  [CONTACTS_CREATE_SUCCESS](state, action) {
    const { contact } = action
    const stateContacts = state.contacts ?? []
    const stateIds = state.ids ?? []
    const existingContactIndex = stateContacts.findIndex(
      c => c.id === contact.id,
    )
    const updatedIds =
      existingContactIndex !== -1 ? stateIds : [...stateIds, contact.id]
    const updatedContacts =
      existingContactIndex === -1
        ? [...stateContacts, { ...contact, loaded: true }]
        : updateItem(stateContacts, existingContactIndex, {
            // existingContactIndex >= 0 here, so element is guaranteed to exist
            ...stateContacts[existingContactIndex]!,
            ...contact,
            loaded: true,
          })
    return {
      ...state,
      saving: false,
      contacts: updatedContacts,
      ids: updatedIds,
    }
  },

  [CONTACTS_IMPORT_SUCCESS](state, action) {
    const { contacts } = action
    const newIds = contacts.map(c => c.id)
    const updatedIds = [
      ...(state.ids ?? []).filter(id => !newIds.some(newId => newId === id)),
      ...newIds,
    ]
    const updatedContacts = [
      ...(state.contacts ?? []).filter(
        c => !contacts.some(newContact => newContact.id === c.id),
      ),
      ...contacts,
    ]
    return {
      ...state,
      saving: false,
      contacts: updatedContacts,
      ids: updatedIds,
    }
  },

  [CONTACTS_MODE_SWITCH](state, action) {
    const { ids, batchMode } = action
    return {
      ...state,
      contacts: switchToEditMode(state.contacts ?? [], ids, batchMode),
    }
  },

  [CONTACTS_VALUE_CHANGE](state, action) {
    const { id, field, value } = action
    return updateContactsValue(state, id, field, value, 'replace')
  },

  [CONTACTS_VALUE_ADD](state, action) {
    const { id, field, value } = action
    return updateContactsValue(state, id, field, value, 'add')
  },

  [CONTACTS_VALUE_REMOVE](state, action) {
    const { id, field, value } = action
    return updateContactsValue(state, id, field, value, 'remove')
  },

  [DATA_LOAD](state, { data: { contacts } }) {
    if (!contacts || !contacts.length) return state
    return {
      ...state,
      contacts: [
        ...(state.contacts ?? []).filter(
          existingContact => !contacts.find(c => existingContact.id === c.id),
        ),
        ...contacts,
      ],
    }
  },

  [CONTACTS_DELETE_SUCCESS](state, action) {
    const { ids } = action
    return {
      ...state,
      contacts: (state.contacts ?? []).filter(
        contact => !ids.includes(contact.id),
      ),
      ids: (state.ids ?? []).filter(id => !ids.includes(id)),
    }
  },

  [CONTACTS_TOGGLE](state, { ids, selectAllCall, deselectAll }) {
    const stateContacts = state.contacts ?? []
    const allContactsAreSelected = !ids.some(id =>
      stateContacts.some(c => c.id === id && !c.selected),
    )
    const toggleState = !allContactsAreSelected
    let selectedAll = selectAllCall ? toggleState : state.selectedAll ?? false
    let deselectedIds = state.deselectedIds ?? []

    if (selectAllCall) {
      deselectedIds = []
    } else if (selectedAll) {
      // row toggles while "select all" is active become exclusions, so the
      // selection keeps covering contacts that are not hydrated yet
      const excluded = new Set(deselectedIds)
      ids.forEach(id => {
        const contact = stateContacts.find(c => c.id === id)
        if (!contact || contact.selected) excluded.add(id)
        else excluded.delete(id)
      })
      deselectedIds = [...excluded]
    }

    if (deselectAll) {
      selectedAll = false
      deselectedIds = []
    }

    return {
      ...state,
      contacts: stateContacts.map(c => {
        let updatedSelected = selectAllCall ? toggleState : !c.selected

        if (deselectAll) {
          updatedSelected = false
        }

        if (ids.includes(c.id)) return { ...c, selected: updatedSelected }
        // return selectAllCall ? { ...c, selected: false } : c
        return c
      }),
      selectedAll: selectedAll,
      deselectedIds,
    }
  },

  [CONTACTS_UNSUBSCRIBE_SUCCESS](state, { ids, listId }) {
    return {
      ...state,
      contacts: (state.contacts ?? []).map(c => {
        if (ids.includes(c.id)) {
          return {
            ...c,
            unsubscribes: {
              global: !listId,
              saved_recipients_ids: [listId],
            },
          }
        } else {
          return c
        }
      }),
    }
  },

  [CONTACT_NOTES_CREATE_SUCCESS](state, { contactId, note }) {
    const contacts = state.contacts ?? []
    const index = contacts.findIndex(c => c.id === contactId)
    const contact = contacts[index]
    if (!contact) return state
    const originalNotes = contact.notes || []
    return {
      ...state,
      contacts: [
        ...contacts.slice(0, index),
        { ...contact, notes: [...originalNotes, note] },
        ...contacts.slice(index + 1),
      ],
    }
  },

  [CONTACT_NOTES_DELETE_SUCCESS](state, { contactId, noteId }) {
    const contacts = state.contacts ?? []
    const index = contacts.findIndex(c => c.id === contactId)
    const contact = contacts[index]
    if (!contact) return state
    return {
      ...state,
      contacts: [
        ...contacts.slice(0, index),
        {
          ...contact,
          notes: (contact.notes ?? []).filter(n => n.id !== noteId),
        },
        ...contacts.slice(index + 1),
      ],
    }
  },

  [CONTACT_NOTES_UPDATE_SUCCESS](state, { contactId, note }) {
    const contacts = state.contacts ?? []
    const index = contacts.findIndex(c => c.id === contactId)
    const contact = contacts[index]
    if (!contact) return state
    const notes = (contact.notes ?? []).map(n => {
      return n.id === note.id ? note : n
    })
    return {
      ...state,
      contacts: [
        ...contacts.slice(0, index),
        { ...contact, notes },
        ...contacts.slice(index + 1),
      ],
    }
  },

  [CONTACT_NOTES_LOAD_SUCCESS](state, { contactId, notes }) {
    const contacts = state.contacts ?? []
    const index = contacts.findIndex(c => c.id === contactId)
    const contact = contacts[index]
    if (!contact) return state
    return {
      ...state,
      contacts: [
        ...contacts.slice(0, index),
        { ...contact, notes },
        ...contacts.slice(index + 1),
      ],
    }
  },

  [CONTACT_ACTIVITY_LOAD](state, { contactId }) {
    return {
      ...state,
      contacts: updateContact(state.contacts ?? [], contactId, contact => ({
        ...contact,
        activityLoading: true,
        activityLoadingError: null,
      })),
    }
  },

  [CONTACT_ACTIVITY_LOAD_SUCCESS](state, { contactId, activity }) {
    return {
      ...state,
      contacts: updateContact(state.contacts ?? [], contactId, contact => ({
        ...contact,
        activity,
        activityLoading: false,
      })),
    }
  },

  [CONTACT_ACTIVITY_LOAD_FAIL](state, { contactId, error }) {
    return {
      ...state,
      contacts: updateContact(state.contacts ?? [], contactId, contact => ({
        ...contact,
        activityLoading: false,
        activityLoadingError: error,
      })),
    }
  },

  [CONTACTS_UPDATE_SUCCESS](state, { contacts }) {
    return {
      ...state,
      saving: false,
      contacts: updateContacts(state.contacts ?? [], contacts),
    }
  },

  [CONTACTS_UPDATE_ALL_SUCCESS](state, { ids, changes }) {
    // patch whichever affected contacts are already hydrated; others will fetch fresh data later
    const affectedIds = new Set(ids)
    // tags_add/tags_remove apply per contact's own tags, unlike the scalar
    // fields which are the same value for everyone - see deriveBulkTagChanges
    const {
      tags_add: tagsAdd,
      tags_remove: tagsRemove,
      ...scalarChanges
    } = changes
    return {
      ...state,
      saving: false,
      contacts: (state.contacts ?? []).map(c => {
        if (!affectedIds.has(c.id)) return c
        let tags = c.tags
        if (tagsAdd?.length || tagsRemove?.length) {
          tags = (c.tags ?? []).filter(t => !tagsRemove?.includes(t))
          tags = [...new Set([...tags, ...(tagsAdd ?? [])])]
        }
        return {
          ...c,
          ...scalarChanges,
          tags,
          pendingChanges: null,
          editMode: false,
        }
      }),
    }
  },

  [CONTACTS_UPDATE_TAGS_SUCCESS](state, { contacts }) {
    return {
      ...state,
      saving: false,
      contacts: updateContacts(state.contacts ?? [], contacts, undefined, [
        'tags',
      ]),
    }
  },

  [CONTACTS_CANCEL_EDITING](state) {
    return {
      ...state,
      contacts: (state.contacts ?? []).map(c => ({
        ...c,
        pendingChanges: null,
        editMode: false,
      })),
    }
  },

  [CONTACTS_ADD_WARNING](state, { warning }) {
    return { ...state, warning }
  },

  [CONTACTS_REMOVE_WARNING](state) {
    return { ...state, warning: null }
  },

  [EMAIL_GROUP_CREATE_SUCCESS](
    state,
    action: {
      emailGroup: RecipientList
    },
  ) {
    return {
      ...state,
      contacts: updateContacts(
        state.contacts ?? [],
        contactsFromList(action.emailGroup),
      ),
    }
  },

  [EMAIL_GROUP_UPDATE_SUCCESS](
    state,
    action: {
      emailGroup: RecipientList
    },
  ) {
    return {
      ...state,
      contacts: updateContacts(
        state.contacts ?? [],
        contactsFromList(action.emailGroup),
      ),
    }
  },

  [PLAYLIST_SHARE_SUCCESS](
    state,
    action: {
      result: RecipientList
      playlist: Playlist
    },
  ) {
    return {
      ...state,
      contacts: updateContacts(
        state.contacts ?? [],
        contactsFromList(action.result, action.playlist),
        {
          id: action.playlist.id,
          name: action.playlist.name,
          tracks: [],
          stats: {},
        },
      ),
    }
  },

  [PLAYLIST_SHARES_LOADING_SUCCESS](state, { responseShares }) {
    const contacts = flatMap(responseShares, share =>
      share.recipients.map(rec => rec.contact),
    ).filter((c): c is Contact => c != null)
    return {
      ...state,
      contacts: updateContacts(state.contacts ?? [], contacts),
    }
  },
})
